import invariant from 'invariant';

import type { ViewportBreakpoint } from '#/theme/theme';
import { breakpointValues } from '#/theme/theme';

/*
 * styled-components mixins
 */
// Generates a media query to target HiDPI devices.
export function hiDPI(ratio = 2) {
  return `
    @media only screen and (min-resolution: ${96 * ratio}dpi),
    only screen and (min-resolution: ${ratio}dppx)
  `;
}

/**
 * @deprecated Use `<FilestackImage>` instead and position it to look like a background
 *
 * The `FilestackImage` component does a series of calculations to optimize
 * the image size and quality delivered each client. We cannot do the same optimizations
 * with background images.
 */
export function retinaBackgroundImage(
  filename: string,
  {
    include3x = true,
    filename2x,
    filename3x,
  }: {
    include3x?: boolean;
    filename2x?: string;
    filename3x?: string;
  } = {},
) {
  let resolvedFilename2x = filename2x;
  let resolvedFilename3x = filename3x;

  if (!resolvedFilename2x) {
    const match = /\.(png|jpg)$/.exec(filename);
    invariant(
      match,
      `Invalid filename "${filename}" File must be a PNG or JPG.`,
    );
    const [, suffix] = match;
    resolvedFilename2x = filename.replace(`.${suffix}`, `@2x.${suffix}`);

    if (include3x && !resolvedFilename3x) {
      resolvedFilename3x = filename.replace(`.${suffix}`, `@3x.${suffix}`);
    }
  }

  let css = `
    background-image: url(${filename});
    ${hiDPI()} {
      background-image: url(${resolvedFilename2x});
    }
  `;

  if (resolvedFilename3x) {
    css += `
      ${hiDPI(3)} {
        background-image: url(${resolvedFilename3x});
      }
    `;
  }

  return css;
}

// @NOTE - We are converting literal CSS var strings
// into hex values - if a new color is needed in this
// function, add it to this map.
const hexCodes = {
  black: '#000',
  white: '#FFF',
  'var(--color-skyBlue)': '#5dc2f1',
  'var(--color-brandBlue)': '#0d73d9',
  'var(--color-brandGreen)': '#68bd53',
  'var(--color-black)': '#000',
  'var(--color-backgroundGrey)': '#fbfbfb',
  'var(--color-alertRed)': '#d0021b',
  'var(--color-secondarySkyBlue)': '#1D91E9',
};

export function getRgbaString(hex: string, opacity: number | string) {
  const hexCode = hex.replace('#', '');
  const fullHex =
    hexCode.length === 3
      ? Array.prototype.map.call(hex, (chr) => chr + chr).join('')
      : // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        (hexCodes[hexCode] ?? `${hexCode}000000`.slice(0, 6));
  const color = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
  const rgb = color
    ? [parseInt(color[1], 16), parseInt(color[2], 16), parseInt(color[3], 16)]
    : [0, 0, 0];
  let a: number;
  if (typeof opacity === 'number') {
    a = opacity > 1 ? opacity / 100 : opacity;
  } else if (opacity.includes('%')) {
    a = parseInt(opacity.replace('%', ''), 10);
  } else {
    a = parseInt(opacity, 10);
  }
  return `${rgb.join(',')},${a}`;
}

export const generateBreakpointValues = (
  property: string,
  values: Partial<Record<keyof typeof breakpointValues, string>>,
) =>
  (
    Object.entries(values) as [breakpoint: ViewportBreakpoint, value: string][]
  ).reduce((acc, [breakpoint, value]) => {
    const mediaQuery = `@media (min-width: ${breakpointValues[breakpoint]}px)`;
    return `
      ${acc}
      ${mediaQuery} {
        ${property}: ${value};
      }
    `;
  }, '');
